import FinanceExpenses from "@/components/FinanceExpenses";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, AlertCircle, QrCode, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { KpiCard, PageHeader, Empty } from "@/components/KpiCard";
import { ChargeBadge } from "@/components/StatusBadge";
import { PaymentDialog } from "@/components/PaymentDialog";
import { useResource } from "@/lib/api";
import { METHODS, brl, fmtDate, todayISO } from "@/lib/constants";

export default function Finance() {
  const [month, setMonth] = useState(todayISO().slice(0, 7));
  const [pay, setPay] = useState(null);
  const { data } = useResource(`/finance?month=${month}`);
  const bm = data?.by_method || {};

  return (
    <div>
      <PageHeader title="Gestão financeira" subtitle="Entradas do período e contas a receber de todos os pacientes">
        <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="w-48 bg-white" data-testid="finance-month-input" />
      </PageHeader>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard label="Recebido no período" value={brl(data?.received)} icon={Wallet} tone="emerald" testId="fin-kpi-received" />
        <KpiCard label="Recebido via PIX" value={brl(bm.pix)} icon={QrCode} tone="cyan" testId="fin-kpi-pix" />
        <KpiCard label="Cartão / Dinheiro / Boleto" value={brl((bm.credito || 0) + (bm.debito || 0) + (bm.dinheiro || 0) + (bm.boleto || 0))} icon={CreditCard} testId="fin-kpi-others" />
        <KpiCard label="A receber (total)" value={brl(data?.receivable)} hint={`${brl(data?.overdue)} em atraso`} icon={AlertCircle} tone="rose" testId="fin-kpi-receivable" />
      </div>
      <div className="mt-6 grid gap-5 xl:grid-cols-2">
        <div className="surface overflow-hidden">
          <h3 className="font-display px-5 pt-5 text-lg font-bold text-slate-900">Extrato de entradas</h3>
          <div className="mt-3 max-h-[520px] overflow-auto">
            {data && data.payments.length === 0 ? <div className="p-5"><Empty>Nenhum pagamento no período</Empty></div> : (
              <table className="w-full text-sm" data-testid="finance-payments-table">
                <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
                  <tr><th className="px-5 py-2.5">Data</th><th className="px-3">Paciente</th><th className="px-3">Forma</th><th className="px-5 text-right">Valor</th></tr>
                </thead>
                <tbody>
                  {(data?.payments || []).map((p) => (
                    <tr key={p.id} className="border-t border-slate-100">
                      <td className="font-mono-num px-5 py-2.5 text-slate-500">{fmtDate(p.date)}</td>
                      <td className="px-3"><Link to={`/pacientes/${p.patient_id}`} className="font-medium text-slate-800 hover:text-sky-700">{p.patient_name}</Link></td>
                      <td className="px-3 text-slate-500">{METHODS[p.method]}</td>
                      <td className="font-mono-num px-5 text-right font-semibold text-emerald-700">{brl(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
        <div className="surface overflow-hidden">
          <h3 className="font-display px-5 pt-5 text-lg font-bold text-slate-900">Contas a receber</h3>
          <div className="mt-3 max-h-[520px] overflow-auto">
            {data && data.open_charges.length === 0 ? <div className="p-5"><Empty>Nada em aberto</Empty></div> : (
              <table className="w-full text-sm" data-testid="finance-open-table">
                <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
                  <tr><th className="px-5 py-2.5">Vencimento</th><th className="px-3">Paciente / descrição</th><th className="px-3 text-right">Em aberto</th><th className="px-3"></th></tr>
                </thead>
                <tbody>
                  {(data?.open_charges || []).map((c) => (
                    <tr key={c.id} className="border-t border-slate-100" data-testid={`open-charge-${c.id}`}>
                      <td className="px-5 py-2.5"><p className="font-mono-num text-slate-600">{fmtDate(c.due_date)}</p><ChargeBadge status={c.status} /></td>
                      <td className="px-3"><Link to={`/pacientes/${c.patient_id}`} className="font-semibold text-slate-800 hover:text-sky-700">{c.patient_name}</Link><p className="text-xs text-slate-500">{c.description}</p></td>
                      <td className="font-mono-num px-3 text-right font-semibold">{brl(c.remaining)}</td>
                      <td className="px-3 text-right"><Button size="sm" variant="ghost" className="text-sky-700 hover:bg-sky-50" onClick={() => setPay(c)} data-testid={`open-charge-pay-${c.id}`}>Dar baixa</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
      <FinanceExpenses key={month} month={month} received={data?.month === month ? data.received : null} />
      <PaymentDialog open={!!pay} onOpenChange={(v) => !v && setPay(null)} patientId={pay?.patient_id} charge={pay} />
    </div>
  );
}
